import { TIMELINE_HEADER_HEIGHT } from '../constants';
import { EditorStarterItem } from '../items/item-type';
import { TRACK_DIVIDER_HEIGHT } from '../state/items';
import { TrackType } from '../state/types';
import { TimelineTrackAndLayout } from '../timeline/utils/drag/calculate-track-heights';

/**
 * Y 좌표를 기반으로 트랙 인덱스를 계산합니다.
 */
export const getTrackIndexFromY = ({
  clientY,
  containerRect,
  tracks,
  scrollTop,
}: {
  clientY: number;
  containerRect: DOMRect;
  tracks: TimelineTrackAndLayout[];
  scrollTop: number;
}): number | null => {
  if (!tracks || tracks.length === 0) return null;

  // scrollTop을 더해서 스크롤된 위치를 보정
  // TIMELINE_HEADER_HEIGHT = TICKS_HEIGHT + TIMELINE_DIVDER_HEIGHT (틱 높이 + marginBottom)
  const relativeY =
    clientY - containerRect.top - TIMELINE_HEADER_HEIGHT + scrollTop;
  if (relativeY < 0) return null;

  for (let i = 0; i < tracks.length; i++) {
    const trackAndLayout = tracks[i];
    if (!trackAndLayout) continue;
    const trackTop = trackAndLayout.top;
    const trackBottom = trackTop + trackAndLayout.height + TRACK_DIVIDER_HEIGHT;
    if (relativeY >= trackTop && relativeY < trackBottom) {
      return i;
    }
  }
  return null;
};

/**
 * 특정 트랙에서 주어진 프레임에 위치한 아이템을 찾습니다.
 */
export const getItemAtFrameInTrack = ({
  track,
  frame,
  items,
}: {
  track: TrackType;
  frame: number;
  items: Record<string, EditorStarterItem>;
}): string | null => {
  if (!track.items) return null;

  for (const itemId of track.items) {
    const item = items[itemId];
    if (!item) continue;
    const itemStart = item.from;
    const itemEnd = itemStart + item.durationInFrames;
    if (frame >= itemStart && frame < itemEnd) {
      return itemId;
    }
  }
  return null;
};

/**
 * 특정 트랙의 특정 프레임 위치가 비어있는지 확인합니다.
 */
export const isTrackPositionAvailable = ({
  track,
  frame,
  durationInFrames,
  items,
}: {
  track: TrackType;
  frame: number;
  durationInFrames: number;
  items: Record<string, EditorStarterItem>;
}): boolean => {
  if (!track.items) return true;

  const newItemStart = frame;
  const newItemEnd = frame + durationInFrames;

  for (const itemId of track.items) {
    const item = items[itemId];
    if (!item) continue;
    const itemStart = item.from;
    const itemEnd = itemStart + item.durationInFrames;

    // 겹치는지 확인
    if (newItemStart < itemEnd && newItemEnd > itemStart) {
      return false;
    }
  }
  return true;
};

/**
 * 에셋 드롭 시 트랙 타겟을 결정합니다.
 *
 * - 첫 번째 트랙 위쪽: 새 트랙 생성 (create-at-top)
 * - 마지막 트랙 아래쪽: 새 트랙 생성 (create-at-bottom)
 * - 기존 트랙 범위: 해당 트랙 인덱스 반환 (existing)
 * - 트랙 사이: 해당 위치에 새 트랙 삽입 (create-between)
 */
export type AssetDropTrackTarget =
  | { type: 'existing'; trackIndex: number }
  | { type: 'create-at-top' }
  | { type: 'create-at-bottom' }
  | { type: 'create-between'; insertAtIndex: number };

// 트랙 위/아래 감지를 위한 여유 공간 (픽셀)
const TRACK_EDGE_THRESHOLD = 20;

export const getAssetDropTrackTarget = ({
  clientY,
  containerRect,
  tracks,
  scrollTop,
}: {
  clientY: number;
  containerRect: DOMRect;
  tracks: TimelineTrackAndLayout[];
  scrollTop: number;
}): AssetDropTrackTarget => {
  if (!tracks || tracks.length === 0) {
    return { type: 'create-at-top' };
  }

  // scrollTop을 더해서 스크롤된 위치를 보정
  const relativeY =
    clientY - containerRect.top - TIMELINE_HEADER_HEIGHT + scrollTop;

  // 첫 번째 트랙 위쪽 체크
  const firstTrack = tracks[0];
  if (firstTrack && relativeY < firstTrack.top - TRACK_EDGE_THRESHOLD) {
    return { type: 'create-at-top' };
  }

  // 마지막 트랙 아래쪽 체크
  const lastTrack = tracks[tracks.length - 1];
  if (lastTrack) {
    const lastTrackBottom = lastTrack.top + lastTrack.height;
    if (relativeY > lastTrackBottom + TRACK_EDGE_THRESHOLD) {
      return { type: 'create-at-bottom' };
    }
  }

  // 기존 트랙 범위 내에서 트랙 인덱스 찾기
  for (let i = 0; i < tracks.length; i++) {
    const trackAndLayout = tracks[i];
    if (!trackAndLayout) continue;
    const trackTop = trackAndLayout.top;
    const trackBottom = trackTop + trackAndLayout.height + TRACK_DIVIDER_HEIGHT;
    // Explicit insertion bands preserve same-track magnetic drops in the body.
    const band = Math.min(6, trackAndLayout.height / 4);
    if (relativeY >= trackTop - band && relativeY <= trackTop + band)
      return { type: 'create-between', insertAtIndex: i };
    if (relativeY >= trackBottom - band && relativeY <= trackBottom + band)
      return { type: 'create-between', insertAtIndex: i + 1 };
    if (relativeY >= trackTop && relativeY < trackBottom) {
      return { type: 'existing', trackIndex: i };
    }
  }

  // 트랙 사이 공간에 있으면 가장 가까운 트랙으로
  // (이 경우는 드물지만 안전하게 처리)
  if (relativeY < 0) {
    return { type: 'create-at-top' };
  }

  return { type: 'create-at-bottom' };
};
